import {AfterViewInit, Directive, ElementRef, Injector, NgZone, OnDestroy, Optional} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {DiMobileSortComponent, MobileListSortAdapter} from 'app/common/mobile/di-mobile-sort.component';
import {MobileActionSheetService} from 'app/common/mobile/mobile-action-sheet.service';
import {EntityListComponent} from 'app/blocks/entity/entity-list.component';
import {EntityCustomListComponent} from 'app/blocks/entity/entity-custom-list.component';

/** The one breakpoint the mobile layer uses (src/styles/_mobile-shell.scss). */
export const MOBILE_QUERY = '(max-width: 599px)';

/**
 * Turns any nz-table into a stack of cards on a phone.
 *
 * A table cannot be read at 390px - the order view alone has seven of them, each scrolling sideways
 * inside its section. The usual fix is to label every cell and restyle rows as cards, but a cell can only
 * be labelled from its column header, and CSS cannot read one element's text into another. So this copies
 * each `th` into the matching `td`'s `data-label`, and _mobile-view.scss does the rest with
 * `content: attr(data-label)`.
 *
 * Doing it here rather than in markup means every table in every screen that imports this gets it - no
 * per-table edits, and nothing to forget on the next table someone adds.
 *
 * Safety: it only ever writes a data-attribute, only below 600px, and outside the Angular zone so the
 * DOM churn of a live table never triggers change detection. Above 600px it does nothing at all.
 */
@Directive({
    // `table[diMobileCards]` opts a plain HTML table in - the legacy grids that never became nz-tables (Run
    // Details' per-stop orders grid).
    selector: 'nz-table, table[diMobileCards]',
    standalone: true
})
export class MobileTableLabelsDirective implements AfterViewInit, OnDestroy {
    private _observer?: MutationObserver;
    private _media?: MediaQueryList;
    private _frame = 0;

    constructor(
        private _el: ElementRef<HTMLElement>,
        private _zone: NgZone,
        @Optional() private _translate?: TranslateService
    ) {}

    ngAfterViewInit(): void {
        if (typeof window === 'undefined' || !window.matchMedia) {
            return;
        }
        this._media = window.matchMedia(MOBILE_QUERY);
        this._media.addEventListener('change', this._onMediaChange);
        this._sync();
    }

    ngOnDestroy(): void {
        this._media?.removeEventListener('change', this._onMediaChange);
        this._stop();
        cancelAnimationFrame(this._frame);
    }

    private _onMediaChange = (): void => this._sync();

    private _sync(): void {
        if (this._media?.matches) {
            this._start();
        } else {
            this._stop();
        }
    }

    private _start(): void {
        this._label();
        if (this._observer) {
            return;
        }
        // nzData arrives asynchronously and rows re-render on every sort/filter, so the labels have to be
        // re-applied whenever the body changes - coalesced into one frame so a large table costs one pass.
        this._zone.runOutsideAngular(() => {
            this._observer = new MutationObserver(() => {
                cancelAnimationFrame(this._frame);
                this._frame = requestAnimationFrame(() => this._label());
            });
            this._observer.observe(this._el.nativeElement, {childList: true, subtree: true, characterData: true});
        });
    }

    private _stop(): void {
        this._observer?.disconnect();
        this._observer = undefined;
    }

    /**
     * Text that belongs to a column heading, and nothing else.
     *
     * A `th` is not just its label. A sortable one wraps the label in `.ant-table-column-title` next to a
     * sorter; a filterable one carries a `di-search-filter` through `nz-th-extra`, which renders a select
     * with its own placeholder. Taking `textContent` gave the Collections screen headings like
     * "CustomerSelect Business Type", and those became the printed label on every card.
     */
    /** How a header cell reads to a person. Also used by the phone "Sort by" control. */
    static labelOf(th: Element): string {
        const NOISE =
            'nz-th-extra, [nz-th-extra], di-search-filter, nz-select, nz-date-picker, input, .ant-table-column-sorter, .ant-table-filter-trigger';
        const source = th.querySelector('.ant-table-column-title') ?? th;
        const walker = document.createTreeWalker(source, NodeFilter.SHOW_TEXT, {
            acceptNode: (node) => (node.parentElement?.closest(NOISE) ? NodeFilter.FILTER_REJECT : NodeFilter.FILTER_ACCEPT)
        });
        const parts: string[] = [];
        while (walker.nextNode()) {
            const text = (walker.currentNode.textContent || '').trim();
            if (text) {
                parts.push(text);
            }
        }
        return parts.join(' ').trim();
    }

    private _label(): void {
        // This nz-table's OWN table. A row can hold a nested table (an order's goods, a batch's orders),
        // and that inner nz-table carries this directive too and labels itself - so a plain
        // querySelectorAll from the host matched both tables' headers and both tables' rows at once, and
        // every card came out labelled with somebody else's columns. Everything below is direct-child
        // scoped for that reason.
        const host = this._el.nativeElement;
        const table = host.tagName === 'TABLE' ? host : host.querySelector('table');
        if (!table) {
            return;
        }
        // Legacy grids write their header cells as td.
        const headers = Array.from(table.querySelectorAll(':scope > thead > tr:first-child > th, :scope > thead > tr:first-child > td')).map((th) =>
            MobileTableLabelsDirective.labelOf(th)
        );
        if (!headers.length) {
            return;
        }
        table.querySelectorAll(':scope > tbody > tr').forEach((row) => {
            // ng-zorro's "No data" row is one full-width cell - labelling it prints a stray column name
            // next to the empty-state illustration.
            if (row.classList.contains('ant-table-placeholder')) {
                return;
            }
            // A group header or an expanded detail row spans the whole table; it has no column of its own,
            // and _mobile-view-parts.scss renders it as the group heading rather than as a card line.
            if (row.children.length === 1 && (row.firstElementChild as HTMLElement)?.hasAttribute('colspan')) {
                return;
            }
            MobileTableLabelsDirective.describeRow(row, headers);
            this._addOpenButton(row as HTMLElement);
        });
    }

    /**
     * Labels every cell of a row and gives the row a card's anatomy.
     *
     * A generic list has no card markup of its own, so the card is read out of the row it already has:
     *
     *   select   the row's selection checkbox           -> head, left
     *   title    the first cell with a real value       -> head, large
     *   status   a cell under a "...Status" heading      -> head, right, as a chip
     *   sub      the next short value                    -> under the title, muted
     *   actions  the row's action menu / buttons         -> footer
     *   empty    a cell with nothing in it               -> not rendered
     *
     * Everything else stays a label / value line. The cells are the desktop's own, `*ngIf`s and all, so
     * whatever Display Settings shows on desktop is exactly what the card shows - no column can go missing
     * and no hidden column can reappear.
     *
     * Roles are data-attributes only, and _mobile-view-parts.scss is the only thing that reads them.
     * Attribute writes are not observed, so describing a row never re-triggers the observer.
     */
    private static describeRow(row: Element, headers: string[]): void {
        // Skip the phone-only View cell _addOpenButton appends: it is not a column, and re-describing it
        // wiped its marker, so every pass appended another one.
        const cells = (Array.from(row.children) as HTMLElement[]).filter((cell) => !cell.hasAttribute('data-mobile-open'));
        let title: HTMLElement | undefined;
        let sub: HTMLElement | undefined;

        // A cell's column is the sum of the spans before it, not its position. Metsa's load-group row has a
        // colSpan="8" filler in the middle, and counting positions labelled everything after it with a heading
        // eight columns early - "Collection Date" over a weight.
        let column = 0;
        cells.forEach((el) => {
            const label = headers[column] || '';
            column += Math.max(1, Number(el.getAttribute('colspan')) || 1);
            if (label && el.dataset.label !== label) {
                el.dataset.label = label;
            }

            const text = (el.textContent || '').replace(/\s+/g, ' ').trim();
            const hasControl = !!el.querySelector(MobileTableLabelsDirective.CONTROLS);
            const empty = !text && !hasControl;

            // A value cell is one that shows something rather than asks for something. A file picker (the
            // Created Manifests "Scan" column) is a control, not a title.
            const isField = !!el.querySelector('input:not([type="checkbox"]):not([type="radio"]), select, textarea');

            let role: string | null = null;
            if (!label && el.querySelector(':scope .ant-table-row-expand-icon')) {
                // A group row's expand toggle (Metsa's loads) sits under a blank heading too.
                role = 'expand';
            } else if (!label && el.classList.contains('ant-table-selection-column')) {
                // The row selector sits under a blank heading. A labelled checkbox column (Drivers'
                // "Available") is a value, and must neither move to the head nor light the card up.
                role = 'select';
            } else if (empty) {
                role = null;
            } else if (el.querySelector('di-entity-action-menu, di-inline-entity-action-menu') || /^(list )?actions?$/i.test(label)) {
                role = 'actions';
            } else if (/status/i.test(label) && text && text.length <= 32 && !isField) {
                role = 'status';
            } else if (!title && text && !isField) {
                title = el;
                role = 'title';
            } else if (title && !sub && text && text.length <= 60 && !isField) {
                sub = el;
                role = 'sub';
            }

            MobileTableLabelsDirective.setAttr(el, 'data-role', role);
            MobileTableLabelsDirective.setAttr(el, 'data-empty', empty ? '' : null);

            // The status chip reuses the kit's .di-mcard-status colours, keyed on the same data-status
            // vocabulary the bespoke cards use ("In Progress" -> IN_PROGRESS).
            const isStatus = role === 'status';
            MobileTableLabelsDirective.setAttr(el, 'data-status', isStatus ? text.toUpperCase().replace(/[\s-]+/g, '_') : null);
            if (el.classList.contains('di-mcard-status') !== isStatus) {
                el.classList.toggle('di-mcard-status', isStatus);
            }
        });
    }

    /**
     * A View button for rows that open on tap but have no row menu to carry one.
     *
     * Roles, and about fifteen other lists, have no action column: the whole desktop row is the link,
     * `(click)` on the tr. A card that opens when you tap anywhere on it gives no sign that it does, so
     * those cards had no View button while every card with a row menu did. The button needs no handler of
     * its own - its click bubbles to the row's, which is exactly what the desktop row click runs.
     *
     * Added only when the row is visibly clickable (the app's di-clickable class, or a pointer cursor) and
     * has no row menu, whose own View button would make this a duplicate. The cell is phone-only and
     * outside Angular's template, so it is removed again at desktop width by the same breakpoint rule.
     */
    private _addOpenButton(row: HTMLElement): void {
        const existing = row.querySelector(':scope > td[data-mobile-open]');
        const hasMenu = !!row.querySelector(':scope > td[data-role="actions"]');
        const clickable = row.classList.contains('di-clickable') || getComputedStyle(row).cursor === 'pointer';
        if (hasMenu || !clickable) {
            existing?.remove();
            return;
        }
        if (existing) {
            return;
        }
        const cell = document.createElement('td');
        cell.setAttribute('data-mobile-open', '');
        cell.setAttribute('data-role', 'open');
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'di-mbtn is-primary';
        const icon = document.createElement('span');
        icon.className = 'di-mi';
        icon.textContent = 'visibility';
        button.appendChild(icon);
        button.appendChild(document.createTextNode(this._translate?.instant('common.view') || 'View'));
        cell.appendChild(button);
        row.appendChild(cell);
    }

    /** Anything that is content even without text: an icon, a control, a menu. */
    private static readonly CONTROLS =
        'input, select, textarea, img, svg, fa-icon, i[nz-icon], .anticon, button, a, nz-switch, label[nz-checkbox], .ant-checkbox, nz-tag, di-boolean-icon, di-entity-action-menu, di-inline-entity-action-menu, driver-popover';

    private static setAttr(el: HTMLElement, name: string, value: string | null): void {
        if (value === null) {
            if (el.hasAttribute(name)) {
                el.removeAttribute(name);
            }
        } else if (el.getAttribute(name) !== value) {
            el.setAttribute(name, value);
        }
    }
}

/**
 * Makes the view/edit sections collapsible on a phone.
 *
 * The order detail screen is ~900 lines of read-only form across sixteen sections; even in one column
 * that is a very long scroll. The design hand-off calls for an accordion with only the section you came
 * for open, which needs open/closed state - so it lives here rather than in the stylesheet.
 *
 * Sections of plain label/value rows stay open - that is the content people came for. Only sections that
 * hold a table start collapsed, since those are the long ones. Above 600px the directive does nothing and
 * the sections render exactly as they always have.
 */
@Directive({
    selector: '.di-view-section, .di-form-section',
    standalone: true
})
export class MobileCollapsibleSectionDirective implements AfterViewInit, OnDestroy {
    private _media?: MediaQueryList;
    private _title?: HTMLElement;

    constructor(
        private _el: ElementRef<HTMLElement>,
        private _zone: NgZone
    ) {}

    ngAfterViewInit(): void {
        if (typeof window === 'undefined' || !window.matchMedia) {
            return;
        }
        this._media = window.matchMedia(MOBILE_QUERY);
        this._media.addEventListener('change', this._onMediaChange);
        this._sync();
    }

    ngOnDestroy(): void {
        this._media?.removeEventListener('change', this._onMediaChange);
        this._title?.removeEventListener('click', this._toggle);
        this._teardown();
    }

    private _onMediaChange = (): void => this._sync();

    private _sync(): void {
        if (this._media?.matches) {
            this._setup();
        } else {
            this._teardown();
        }
    }

    private _setup(): void {
        const host = this._el.nativeElement;
        // R1: opt-in per screen. This directive now travels with MOBILE_LIST_DIRECTIVES into every list
        // module, which also declares that feature's create/edit and view screens - and those have not
        // asked for the phone layer. Without this check their sections would get a clickable title that
        // toggles a class no stylesheet reads, which is a dead tap rather than a collapse.
        if (!host.closest('.di-mobile-ready')) {
            return;
        }
        const title = host.querySelector<HTMLElement>(':scope > .di-view-section-title, :scope > .di-form-section-title');
        if (!title || this._title) {
            return;
        }
        this._title = title;
        title.setAttribute('role', 'button');
        title.setAttribute('tabindex', '0');
        host.classList.add('di-collapsible');

        // Plain label/value sections stay open - they are the content people came to read, and collapsing
        // them just adds taps. Only the table-bearing sections (Claused, Refusal, Order Documents, Pod
        // Info, Invoice Info) start shut, because those are the long ones.
        this._setExpanded(!host.querySelector('nz-table'));

        this._zone.runOutsideAngular(() => title.addEventListener('click', this._toggle));
    }

    private _teardown(): void {
        const host = this._el.nativeElement;
        this._title?.removeEventListener('click', this._toggle);
        this._title?.removeAttribute('role');
        this._title?.removeAttribute('tabindex');
        this._title?.removeAttribute('aria-expanded');
        this._title = undefined;
        host.classList.remove('di-collapsible', 'is-collapsed');
    }

    private _toggle = (): void => {
        this._setExpanded(this._el.nativeElement.classList.contains('is-collapsed'));
    };

    private _setExpanded(expanded: boolean): void {
        this._el.nativeElement.classList.toggle('is-collapsed', !expanded);
        this._title?.setAttribute('aria-expanded', String(expanded));
    }
}

/**
 * A sort icon beside the list's search box on a phone.
 *
 * Below 600px the list is cards and its header row is not rendered, so the column sort arrows go with it.
 * This puts one small icon next to the search box - on every list that opted into the phone layer, since
 * every entity list sorts at least by id - and opens "Sort by" (DiMobileSortComponent) as a sheet from it.
 *
 * The list table is the first nz-table on the page with sortable columns of its own, or failing that the
 * first in the list content when the list itself can sort by id (EntityListComponent /
 * EntityCustomListComponent - resolved from the injector at runtime, not imported as constructor
 * types, so this common file does not pull the entity base classes into its own load order).
 *
 * A screen with no search box gets the icon at the end of its header. Travels with MOBILE_VIEW_DIRECTIVES,
 * so no template changes; nothing runs or is added at 600px and wider.
 */
@Directive({
    selector: 'nz-table',
    standalone: true
})
export class MobileTableSortDirective implements AfterViewInit, OnDestroy {
    private _media?: MediaQueryList;
    private _button?: HTMLButtonElement;
    private _row?: HTMLElement;
    private _paired?: HTMLElement;
    private _initialSort?: string;
    private _list?: MobileListSortAdapter;
    private _timer?: ReturnType<typeof setTimeout>;

    constructor(
        private _el: ElementRef<HTMLElement>,
        private _injector: Injector,
        private _zone: NgZone,
        @Optional() private _translate?: TranslateService
    ) {}

    ngAfterViewInit(): void {
        if (typeof window === 'undefined' || !window.matchMedia) {
            return;
        }
        this._media = window.matchMedia(MOBILE_QUERY);
        this._media.addEventListener('change', this._onMediaChange);
        this._sync();
    }

    ngOnDestroy(): void {
        clearTimeout(this._timer);
        this._media?.removeEventListener('change', this._onMediaChange);
        this._teardown();
    }

    private _onMediaChange = (): void => this._sync();

    private _sync(): void {
        clearTimeout(this._timer);
        if (this._media?.matches) {
            // After ng-zorro has marked the sortable header cells.
            this._timer = setTimeout(() => this._setup());
        } else {
            this._teardown();
        }
    }

    private _setup(): void {
        const host = this._el.nativeElement;
        if (this._button || host.closest('.ant-modal, .ant-drawer')) {
            return;
        }
        const page = host.closest<HTMLElement>('.di-page.di-mobile-ready');
        // A list page, not a create / edit / view screen with a table in one of its sections. Most lists mark their
        // content .di-list; Collections and Deliveries use a bare .di-page-content, so it is recognised by what it
        // is not.
        // (Reports marks its whole page di-view although its content is a .di-list, so an explicit .di-list wins.)
        const isList = !!page?.querySelector('.di-page-content.di-list') || !page?.matches('.di-view, .di-edit');
        if (!page || !isList || host.closest('.di-page-content.di-view, .di-page-content.di-edit, .di-view-section, .di-form-section')) {
            return;
        }
        const content = page.querySelector<HTMLElement>('.di-page-content') ?? page;
        this._list = this._listAdapter();
        const tables = Array.from(page.querySelectorAll<HTMLElement>('nz-table')).filter((t) => !t.closest('.ant-modal, .ant-drawer'));
        const listTable =
            tables.find((table) => DiMobileSortComponent.sortableHeaders(table).length > 0) ??
            (this._list ? tables.find((table) => content.contains(table)) : undefined);
        if (listTable !== host) {
            return;
        }
        const header = page.querySelector<HTMLElement>('.di-page-header, .di-page-header-flexible, .di-page-header-tall');
        if (!header) {
            return;
        }

        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'di-msort-btn';
        button.setAttribute('aria-label', this._translate?.instant('mobileKit.sortBy') ?? 'Sort by');
        button.innerHTML = '<span class="di-mi">swap_vert</span>';
        this._button = button;

        // Where it goes, in order: beside a search box that is showing; with the header's own buttons; beside the
        // "Filters" toggle when the header's buttons are folded behind it; on the title line. Never a row of its own.
        const search = Array.from(header.querySelectorAll<HTMLElement>('di-search-bar')).find((el) => !!el.querySelector('input'));
        const visible = (el: HTMLElement) => el.getClientRects().length > 0 && !el.closest('.di-mheader-filters');
        const headerButtons = Array.from(header.querySelectorAll<HTMLElement>('.ant-btn')).filter(visible);
        const filtersToggle = header.querySelector<HTMLElement>('.di-mheader-filters-toggle');
        const title = header.querySelector<HTMLElement>('.page-title');
        if (search?.parentElement) {
            this._pair(search, button);
        } else if (headerButtons.length) {
            const last = headerButtons[headerButtons.length - 1];
            button.classList.add('is-inline');
            last.parentElement?.insertBefore(button, last.nextSibling);
        } else if (filtersToggle) {
            this._pair(filtersToggle, button).classList.add('has-toggle');
        } else if (title?.parentElement) {
            button.classList.add('is-inline', 'is-title');
            title.parentElement.insertBefore(button, title.nextSibling);
        } else {
            const row = document.createElement('div');
            row.className = 'di-msearch-row is-icon-only';
            row.appendChild(button);
            header.appendChild(row);
            this._row = row;
        }
        this._markActive();

        this._zone.runOutsideAngular(() => button.addEventListener('click', this._open));
    }

    private _teardown(): void {
        this._button?.removeEventListener('click', this._open);
        if (this._row && this._paired) {
            this._row.parentElement?.insertBefore(this._paired, this._row);
        }
        this._row?.remove();
        this._button?.remove();
        this._row = undefined;
        this._paired = undefined;
        this._button = undefined;
    }

    /** Share a row with an existing header control (the search box, or the Filters toggle): it takes the width. */
    private _pair(control: HTMLElement, button: HTMLElement): HTMLElement {
        const row = document.createElement('div');
        row.className = 'di-msearch-row';
        control.parentElement?.insertBefore(row, control);
        row.appendChild(control);
        row.appendChild(button);
        this._paired = control;
        this._row = row;
        return row;
    }

    private _open = (): void => {
        this._zone.run(() => {
            const sheets = this._injector.get(MobileActionSheetService);
            sheets
                .openSort({table: this._el.nativeElement, labelOf: (th) => MobileTableLabelsDirective.labelOf(th), list: this._list})
                .afterClose.subscribe(() => setTimeout(() => this._markActive()));
        });
    };

    /**
     * The dot on the icon means "you changed the order". Compared with what the list opened with rather than with
     * "some column is sorted": many lists start sorted by a column (id ascending lights its arrow), and the dot on an
     * untouched list said something had been changed when nothing had.
     */
    private _markActive(): void {
        const now = this._sortState();
        if (this._initialSort === undefined) {
            this._initialSort = now;
        }
        this._button?.classList.toggle('is-active', now !== this._initialSort);
    }

    private _sortState(): string {
        const sorted = DiMobileSortComponent.sortableHeaders(this._el.nativeElement)
            .map((th) => [MobileTableLabelsDirective.labelOf(th), DiMobileSortComponent.directionOf(th)])
            .find(([, direction]) => !!direction);
        const list = this._list?.current();
        return sorted ? sorted.join(':') : list ? `${list.field}:${list.direction}` : '';
    }

    /** The list screen's own sort, when the table sits inside one of the entity list base classes. */
    private _listAdapter(): MobileListSortAdapter | undefined {
        const entityList = this._injector.get(EntityListComponent, null, {optional: true}) as any;
        const customList = entityList ? null : (this._injector.get(EntityCustomListComponent, null, {optional: true}) as any);
        const list = entityList ?? customList;
        if (!list || typeof list.sortBy !== 'function') {
            return undefined;
        }
        // An entity list always sends its sort column with the page request. A custom list only does if its own
        // loadRequests reads the sort - most reports post a date range and nothing else, and offering "ID" there
        // would be a control that reloads the same rows. The base loadRequests loads nothing.
        if (customList) {
            const load = customList.loadRequests;
            const readsSort = typeof load === 'function' && /getFilterMap|getSortParam|sortName|sortParams/.test(String(load));
            if (!readsSort) {
                return undefined;
            }
        }
        return {
            current: () => ({field: list.sortName, direction: list.sortDir ?? null}),
            sort: (field, direction) => {
                list.sortBy(field, direction);
                // EntityCustomListComponent reloads from sortBy; EntityListComponent reloads from its view params.
                if (entityList && typeof entityList.updateListViewPage === 'function') {
                    entityList.updateListViewPage();
                }
            }
        };
    }
}

/** Import this into a screen's `imports` to get the card labels, the collapsible sections and the phone sort. */
export const MOBILE_VIEW_DIRECTIVES = [MobileTableLabelsDirective, MobileCollapsibleSectionDirective, MobileTableSortDirective];
